import React, { useState } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { Bookmark } from '../types/bookmarks';
import { stripHtml, normalizeUrl } from '../utils/bookmarkUtils';
import {
  useHighlightScroll,
  blockPulseHighlight,
} from '../hooks/useHighlightScroll';
import BookmarkActionsMenu from './BookmarkActionsMenu';

interface BookmarkCompactItemProps {
  bookmark: Bookmark;
  onClick?: () => void;
  onEditStart?: () => void;
  highlighted?: boolean;
  onHighlightComplete?: () => void;
}

/**
 * Compact bookmark item: name as link on first line, notes underneath (up to 120 chars),
 * action buttons always right-justified. Tight padding for fast scanning.
 */
const BookmarkCompactItem: React.FC<BookmarkCompactItemProps> = ({
  bookmark,
  onClick,
  onEditStart,
  highlighted,
  onHighlightComplete,
}) => {
  const [notesExpanded, setNotesExpanded] = useState(false);
  const { ref: itemRef } = useHighlightScroll<HTMLDivElement>(
    highlighted,
    onHighlightComplete,
  );

  const plainNotes = stripHtml(bookmark.bookmark_notes) || '';
  const hasMore = plainNotes.length > 120;
  const notesHint =
    plainNotes.length > 120 ? plainNotes.slice(0, 120) + '...' : plainNotes;
  const notesDisplay = notesExpanded ? plainNotes : notesHint;
  const href = normalizeUrl(bookmark.bookmark_url);

  return (
    <Box
      ref={itemRef}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1,
        py: 0.5,
        cursor: onClick ? 'pointer' : undefined,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        mb: 0.5,
        '&:hover': {
          bgcolor: 'action.hover',
        },
        ...(highlighted && {
          animation: `${blockPulseHighlight} 3.5s ease-in-out`,
        }),
      }}
    >
      {/* Left: name + notes stacked */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {href ? (
          <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            variant="body2"
            sx={{ fontWeight: 500 }}
            onClick={(e) => e.stopPropagation()}
          >
            {bookmark.bookmark_name}
          </Link>
        ) : (
          <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
            {bookmark.bookmark_name}
          </Typography>
        )}
        {plainNotes && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="span"
            sx={{
              display: 'block',
              overflow: notesExpanded ? 'visible' : 'hidden',
              textOverflow: notesExpanded ? undefined : 'ellipsis',
              whiteSpace: notesExpanded ? 'normal' : 'nowrap',
              fontStyle: 'italic',
              mt: 0.25,
            }}
          >
            {notesDisplay}
            {hasMore && !notesExpanded && (
              <Typography
                component="span"
                variant="caption"
                color="primary"
                sx={{ ml: 1, cursor: 'pointer', fontStyle: 'normal' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setNotesExpanded(true);
                }}
              >
                [more]
              </Typography>
            )}
            {hasMore && notesExpanded && (
              <Typography
                component="span"
                variant="caption"
                color="primary"
                sx={{ ml: 1, cursor: 'pointer', fontStyle: 'normal' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setNotesExpanded(false);
                }}
              >
                [less]
              </Typography>
            )}
          </Typography>
        )}
      </Box>

      {/* Right: action buttons always pinned right */}
      <Box
        sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', pt: 0.25 }}
      >
        <BookmarkActionsMenu
          bookmark={bookmark}
          onEdit={() => onEditStart?.()}
          iconSize={16}
        />
      </Box>
    </Box>
  );
};

export default BookmarkCompactItem;
